<template>
  <v-app>
    <app-top-bar :class="hasBackgroundImage ? '' : 'bg-background'" :path-return="pathPage">{{
      $t("bikePhoto.pageTitle")
    }}</app-top-bar>
    <v-main
      :style="mainStyle"
      :class="hasBackgroundImage ? '' : 'bg-background'"
      class="text-on-surface-variant"
    >
      <v-container v-if="!showComplete && !util.isMobile">
        <media-desktop-handoff :description="$t('bikePhoto.handoffDescription')" />
      </v-container>

      <v-container v-if="!showComplete && util.isMobile">
        <h1 class="page-title mb-6">
          {{ title }}
        </h1>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <p class="mb-0" v-html="subtitle" />

        <v-sheet v-if="!isPhotoAvailable" class="d-flex flex-column ga-9 mt-12">
          <div v-for="(rule, index) in rules" :key="'rule' + index" class="d-flex flex-column">
            <img
              class="mx-auto mb-3"
              :src="rule.img"
              width="70"
              :class="hasBackgroundImage ? 'invert' : ''"
            />
            <!-- eslint-disable-next-line vue/no-v-html -->
            <p class="text-center mb-0" v-html="rule.text" />
          </div>
        </v-sheet>

        <img
          v-if="isPhotoAvailable"
          class="img-preview rounded-lg mt-6"
          :src="photoUrl"
          style="max-width: 500px"
        />

        <image-uploader
          ref="photoInput"
          accept="image/jpeg,image/png"
          :capture="'environment'"
          :max-height="1920"
          :max-width="1920"
          output-format="file"
          :preview="false"
          :quality="0.9"
          @input="onPhotoInput"
        />
        <v-overlay :model-value="isProcessingMedia" class="align-center justify-center"
          ><v-progress-circular color="primary" indeterminate size="64"></v-progress-circular
        ></v-overlay>
      </v-container>
      <v-container v-if="showComplete">
        <phase-completed />
      </v-container>
    </v-main>

    <app-footer v-if="util.isMobile">
      <template v-if="!isPhotoAvailable">
        <continue-footer-button color="primary" @click="onPromptPhoto">
          {{ $t("bikePhoto.continueButton") }}
        </continue-footer-button>
      </template>
      <template v-else>
        <footer-button color="primary" :loading="isAdvancing" @click="advance">
          {{ $t("bikePhoto.looksGoodButton") }}
        </footer-button>
        <footer-button variant="text" :disabled="isAdvancing" @click="onPromptPhoto">
          {{ $t("bikePhoto.takeAnotherButton") }}
        </footer-button>
      </template>
    </app-footer>
  </v-app>
</template>

<script setup lang="ts">
  import { computed, ref, useTemplateRef } from "vue";
  import { useI18n } from "vue-i18n";
  import ImageUploader from "vue3-image-upload-resize";

  import bike from "@/assets/images/icons/bike.svg";
  import contrast from "@/assets/images/icons/contrast.svg";
  import sun from "@/assets/images/icons/sun.svg";
  import { usePageBackground } from "@/composables/usePageBackground";
  import { useStepFlow } from "@/composables/useStepFlow";
  import { useUtils } from "@/composables/useUtils";
  import { useApiStore } from "@/stores/api";
  import { useAppStore } from "@/stores/app";

  const { t } = useI18n();

  const util = useUtils();
  const api = useApiStore();
  const app = useAppStore();
  const { mainStyle, hasBackgroundImage } = usePageBackground();

  const isProcessingMedia = ref(false);
  const photo = ref(null);
  const photoUrl = computed(() => URL.createObjectURL(photo.value));
  const pathPage = "/photo";

  const {
    isAdvancing,
    showComplete,
    advance: advanceFlow,
  } = useStepFlow(pathPage, () => isComplete.value);

  const photoInputRef = useTemplateRef("photoInput");
  const isComplete = computed(() => api.isItemComplete("photo"));
  const isPhotoAvailable = computed(() => !isComplete.value && photo.value !== null);

  const title = computed(() =>
    isPhotoAvailable.value ? t("bikePhoto.titleReview") : t("bikePhoto.titleTake"),
  );
  const subtitle = computed(() =>
    isPhotoAvailable.value ? t("bikePhoto.subtitleReview") : t("bikePhoto.subtitleTake"),
  );

  async function onPhotoInput(file) {
    isProcessingMedia.value = true;
    await new Promise((resolve) => setTimeout(resolve, 150)); // Garante o render do overlay na tela
    try {
      if (!(file instanceof File)) return;
      photo.value = file;
    } finally {
      isProcessingMedia.value = false;
    }
  }

  async function onPromptPhoto() {
    if (isComplete.value) {
      await advanceFlow();
      return;
    }

    const input = photoInputRef.value.$el.querySelector("input[type=file]");
    input.click();
  }

  async function advance() {
    try {
      await advanceFlow(async () => {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          if (photo.value) reader.readAsDataURL(photo.value);
        });
        await api.sendBikeMedia({ file: photo.value, type: "BIKE_RIGHT" });
      });
    } catch (error) {
      console.error(error);
    }
  }

  const rules = [
    {
      img: sun,
      text: t("bikePhoto.rule1Text"),
    },
    {
      img: contrast,
      text: t("bikePhoto.rule2Text"),
    },
    {
      img: bike,
      text: t("bikePhoto.rule3Text"),
    },
  ];
</script>

<style scoped>
  :deep(.img-preview) {
    height: 100% !important;
    width: 100% !important;
  }

  :deep(.fileinput) {
    display: none;
  }

  p {
    margin-bottom: 1.25rem;
  }

  .invert {
    filter: invert(1);
  }
</style>
